import { useCallback, useEffect, useState } from "react";

const PADDING = 8;

/**
 * Shared measurement hook for the Guided Tour — resolves the current
 * step's target element to a viewport-relative rect (padded), and keeps
 * it in sync on resize/scroll. Shared by `TourSpotlightOverlay` (the
 * dimming/cutout) and `TourStepPopover` (the callout card position) so
 * they can never drift out of sync with each other.
 */
export function useTourTargetRect(targetTestId) {
  const [rect, setRect] = useState(null);

  const measure = useCallback(() => {
    if (!targetTestId) {
      setRect(null);
      return;
    }
    const el = document.querySelector(`[data-testid="${targetTestId}"]`);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top - PADDING, left: r.left - PADDING, width: r.width + PADDING * 2, height: r.height + PADDING * 2 });
  }, [targetTestId]);

  useEffect(() => {
    const raf1 = requestAnimationFrame(() => requestAnimationFrame(measure));

    let ticking = false;
    const onRecompute = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        measure();
        ticking = false;
      });
    };
    window.addEventListener("resize", onRecompute);
    window.addEventListener("scroll", onRecompute, true);
    return () => {
      cancelAnimationFrame(raf1);
      window.removeEventListener("resize", onRecompute);
      window.removeEventListener("scroll", onRecompute, true);
    };
  }, [measure]);

  return rect;
}
